import {
  ExternalLink,
  Link2,
  LucideIcon,
  Pencil,
  Plus,
  Save,
  Trash2,
  Upload,
  X,
} from "lucide-react";

const ICONS: Record<string, LucideIcon> = {
  "external-link": ExternalLink,
  link: Link2,
  pencil: Pencil,
  plus: Plus,
  save: Save,
  "trash-2": Trash2,
  upload: Upload,
  x: X,
};

export function AcademyIcon({
  name,
  size = 20,
  color = "currentColor",
}: {
  name: string;
  size?: number;
  color?: string;
}) {
  const Glyph = ICONS[name];
  if (!Glyph) return null;
  return <Glyph size={size} color={color} strokeWidth={2} aria-hidden />;
}

const buttonFills: Record<string, Record<string, any>> = {
  primary: {
    background: "var(--it-navy-800)",
    color: "#fff",
    border: "1px solid var(--it-navy-800)",
  },
  secondary: {
    background: "var(--surface-card)",
    color: "var(--it-navy-800)",
    border: "1px solid var(--it-navy-800)",
  },
  destructive: {
    background: "var(--it-danger-500)",
    color: "#fff",
    border: "1px solid var(--it-danger-500)",
  },
  ghost: {
    background: "transparent",
    color: "var(--it-navy-800)",
    border: "1px solid transparent",
  },
};

const buttonSizes = {
  md: { padding: "10px 20px", font: "var(--fw-semibold) 1rem/1 var(--font-body)", gap: 8, icon: 16 },
  sm: { padding: "8px 12px", font: "var(--fw-semibold) 0.8rem/1 var(--font-body)", gap: 8, icon: 16 },
};

export function AcademyButton({
  children,
  variant = "primary",
  size = "md",
  icon,
  iconPosition = "end",
  disabled,
  onClick,
  label,
}: {
  children?: any;
  variant?: "primary" | "secondary" | "destructive" | "ghost";
  size?: "md" | "sm";
  icon?: string;
  iconPosition?: "start" | "end";
  disabled?: boolean;
  onClick?: () => void;
  label?: string;
}) {
  const s = buttonSizes[size];
  const glyph = icon ? <AcademyIcon name={icon} size={s.icon} color="currentColor" /> : null;
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: children ? s.gap : 0,
        padding: children ? s.padding : "8px",
        font: s.font,
        borderRadius: "var(--radius-md)",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.45 : 1,
        transition: "var(--transition-control)",
        whiteSpace: "nowrap",
        ...buttonFills[variant],
      }}
    >
      {iconPosition === "start" && glyph}
      {children}
      {iconPosition === "end" && glyph}
    </button>
  );
}

export function AcademyCard({
  title,
  children,
  padding = "22px 24px",
}: {
  title?: string;
  children: any;
  padding?: string;
}) {
  return (
    <section
      style={{
        background: "var(--surface-card)",
        borderRadius: "var(--radius-lg)",
        boxShadow: "var(--shadow-card)",
        padding,
      }}
    >
      {title && (
        <header style={{ marginBottom: "var(--space-5)" }}>
            <h3
            style={{
              margin: 0,
              font: "600 1.125rem/1.3 var(--font-body)",
              color: "var(--text-heading)",
              letterSpacing: "var(--ls-tight)",
            }}
          >
            {title}
          </h3>
        </header>
      )}
      {children}
    </section>
  );
}

export function AcademyStatusPill({
  status,
  label,
}: {
  status: string;
  label?: string;
}) {
  const key = String(status).toLowerCase();
  const published = key === "published";
  return (
    <span
      style={{
        display: "inline-block",
        padding: "5px 14px",
        borderRadius: 999,
        font: "var(--fw-semibold) var(--fs-label)/1.2 var(--font-body)",
        textTransform: "capitalize",
        background: published ? "var(--status-published)" : "transparent",
        color: published ? "#fff" : "var(--status-draft)",
        border: published ? "1px solid transparent" : "1px solid var(--status-draft)",
      }}
    >
      {label || key}
    </span>
  );
}

export function AcademyToggle({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <span
      role="switch"
      aria-checked={checked}
      tabIndex={0}
      onClick={() => !disabled && onChange(!checked)}
      onKeyDown={(event) => {
        if (disabled) return;
        if (event.key === " " || event.key === "Enter") {
          event.preventDefault();
          onChange(!checked);
        }
      }}
      style={{
        width: 46,
        height: 26,
        borderRadius: 999,
        display: "inline-block",
        position: "relative",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.45 : 1,
        background: checked ? "var(--status-published)" : "var(--it-grey-300)",
        transition: "var(--transition-control)",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 3,
          left: checked ? 23 : 3,
          width: 20,
          height: 20,
          borderRadius: "50%",
          background: "#fff",
          transition: "var(--transition-control)",
        }}
      />
    </span>
  );
}

const fieldLabel = "block text-sm font-medium text-gray-700 mb-1";
const fieldControl =
  "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 bg-white";

export function AcademyTextField({
  label,
  value,
  onChange,
  placeholder,
  required,
  type = "text",
  hint,
  icon,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  type?: string;
  hint?: string;
  icon?: string;
}) {
  return (
    <div>
      <label className={fieldLabel}>
        {label}
        {required ? " *" : ""}
      </label>
      <span className="flex items-center gap-2">
        <input
          type={type}
          value={value}
          placeholder={placeholder}
          required={required}
          onChange={(event) => onChange(event.target.value)}
          className={fieldControl}
        />
        {icon && <AcademyIcon name={icon} size={18} color="#183853" />}
      </span>
      {hint && (
        <div
          className="mt-1 text-xs text-gray-500"
        >
          {hint}
        </div>
      )}
    </div>
  );
}

export function AcademyTextArea({
  label,
  value,
  onChange,
  placeholder,
  required,
  rows = 4,
  maxLength,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  rows?: number;
  maxLength?: number;
}) {
  return (
    <div>
      <label className={fieldLabel}>
        {label}
        {required ? " *" : ""}
      </label>
      <textarea
        rows={rows}
        value={value}
        placeholder={placeholder}
        required={required}
        maxLength={maxLength}
        onChange={(event) => onChange(event.target.value)}
        className={`${fieldControl} min-h-[100px] resize-y`}
      />
      {maxLength ? (
        <div className="mt-1 text-right text-xs text-gray-500">
          {(value || "").length}/{maxLength}
        </div>
      ) : null}
    </div>
  );
}

export function AcademyImageUpload({
  label,
  value,
  onClick,
  height = 140,
  placeholder = "program cover image",
}: {
  label: string;
  value: string;
  onClick: () => void;
  height?: number;
  placeholder?: string;
}) {
  return (
    <div>
      <div className={fieldLabel}>{label}</div>
      <div
        onClick={onClick}
        style={{
          height,
          borderRadius: "var(--radius-md)",
          cursor: "pointer",
          overflow: "hidden",
          border: value ? "1px solid #cfd8de" : "1px dashed #a9bccb",
          background: value
            ? `center/cover no-repeat url(${value})`
            : "repeating-linear-gradient(135deg,#eef3f7,#eef3f7 8px,#e4ecf3 8px,#e4ecf3 16px)",
          display: "grid",
          placeItems: "center",
        }}
      >
        {!value && (
          <div style={{ display: "grid", justifyItems: "center", gap: 8 }}>
            <AcademyIcon name="upload" size={26} color="var(--it-teal-600)" />
            <span
              style={{
                font: "var(--fw-regular) var(--fs-meta)/1.3 var(--font-mono)",
                color: "var(--text-muted)",
              }}
            >
              {placeholder}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export type AcademyColumn<T> = {
  key: string;
  label: string;
  width?: string;
  align?: "left" | "right";
  render?: (row: T) => any;
};

export function AcademyTable<T extends Record<string, any>>({
  columns,
  rows,
  rowKey = "id",
  empty,
  style,
}: {
  columns: AcademyColumn<T>[];
  rows: T[];
  rowKey?: string;
  empty: string;
  style?: Record<string, any>;
}) {
  const grid = columns.map((column) => column.width || "minmax(0,1fr)").join(" ");
  return (
    <div style={style}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: grid,
          gap: 16,
          alignItems: "center",
          padding: "0 14px 12px",
          borderBottom: "1px solid var(--border-hairline)",
        }}
      >
        {columns.map((column) => (
          <div
            key={column.key}
            style={{
              font: "var(--fw-bold) var(--fs-label)/1.2 var(--font-body)",
              color: "var(--text-heading)",
              textAlign: column.align || "left",
            }}
          >
            {column.label}
          </div>
        ))}
      </div>
      {rows.length === 0 && (
        <div style={{ padding: "26px 14px", font: "var(--text-default)", color: "var(--text-muted)" }}>
          {empty}
        </div>
      )}
      {rows.map((row, index) => (
        <div
          key={row[rowKey] ?? index}
          style={{
            display: "grid",
            gridTemplateColumns: grid,
            gap: 16,
            alignItems: "center",
            padding: "14px",
            borderBottom: "1px solid var(--border-hairline)",
          }}
        >
          {columns.map((column) => (
            <div
              key={column.key}
              style={{
                font: "var(--text-default)",
                color: "var(--text-body)",
                textAlign: column.align || "left",
                minWidth: 0,
              }}
            >
              {column.render ? column.render(row) : row[column.key]}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export function AcademyModal({
  open,
  title,
  children,
  actions,
  onClose,
  width = 520,
}: {
  open: boolean;
  title?: any;
  children?: any;
  actions?: any;
  onClose: () => void;
  width?: number | string;
}) {
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={typeof title === "string" ? title : undefined}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 80,
        background: "rgba(15,38,55,.55)",
        display: "grid",
        placeItems: "center",
        padding: 24,
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          width,
          maxWidth: "100%",
          position: "relative",
          background: "var(--surface-card)",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-menu)",
          padding: "26px 28px",
          fontFamily: "Lato, system-ui, sans-serif",
          fontSize: "0.8rem",
          lineHeight: 1.5,
          color: "var(--text-body, #183853)",
        }}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          style={{
            position: "absolute",
            top: 16,
            right: 16,
            display: "grid",
            placeItems: "center",
            width: 32,
            height: 32,
            padding: 0,
            border: 0,
            borderRadius: 6,
            background: "transparent",
            cursor: "pointer",
            color: "#183853",
          }}
        >
          <AcademyIcon name="x" size={18} color="#183853" />
        </button>
        {title && (
          <h3
            style={{
              margin: "0 36px 12px 0",
              font: "700 1.25rem/1.2 Lato, system-ui, sans-serif",
              color: "#183853",
            }}
          >
            {title}
          </h3>
        )}
        <div style={{ font: "400 0.8rem/1.5 Lato, system-ui, sans-serif", color: "#183853" }}>{children}</div>
        {actions && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 12,
              marginTop: 26,
            }}
          >
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}
